import { Action, Thunk, thunk, action, Computed, computed } from 'easy-peasy';
import orderBy from 'lodash/orderBy';
import { ToDoController } from './todoController';
import HttpClientWithRetry from '../../../http/client/HttpClientWithRetry';
import { FormattedTodo, StatusEnum, StatusOptions, TodoResponse, TodoStats } from '../types';
import { HttpStoreModel } from '../../../http/models/httpModel';
import { formatData } from '../utils/todoFormatter';
// eslint-disable-next-line import/no-cycle
import { StoreModel } from '../../../models/storeModel';
import { Appointment } from '../../../firebase/firestore/documents/candidate';
import { ToDoTitle } from '../../../firebase/firestore/documents/lookups';

export type TodoStoreModel = {
  todo: TodoModel;
};
const defaultStatusFilters = [StatusOptions.TODO, StatusOptions.IN_PROGRESS_ME];
export interface TodoModel {
  todos: FormattedTodo[];
  counts: TodoStats | null;
  isLoading: boolean;
  isRefetching: boolean;
  openTodoSummary: boolean;
  filterByStatus: string;
  currentPage: number;
  pageSize: number;
  openCandidateMessagingModal: boolean;
  messagingViewModal: boolean;
  selectedCandidate: string | null;
  selectedTodo: FormattedTodo | null;
  statusFilters: StatusOptions[];
  interviewCounts: TodoStats | null;

  setFilterByStatus: Action<TodoModel, string>;
  setOpenTodoSummary: Action<TodoModel, boolean>;
  setTodos: Action<TodoModel, FormattedTodo[]>;
  setCounts: Action<TodoModel, TodoStats>;
  setLoading: Action<TodoModel, boolean>;
  setRefetching: Action<TodoModel, boolean>;
  setCurrentPage: Action<TodoModel, number>;
  setPageSize: Action<TodoModel, number>;
  setOpenCandidateMessagingModal: Action<TodoModel, boolean>;
  setMessagingViewModal: Action<TodoModel, boolean>;
  setSelectedCandidate: Action<TodoModel, string | null>;
  setSelectedTodo: Action<TodoModel, FormattedTodo | null>;
  setStatusFilters: Action<TodoModel, StatusOptions[]>;
  setInterviewCounts: Action<TodoModel, TodoStats>;
  resetState: Action<TodoModel>;

  paginatedTodos: Computed<TodoModel, { data: FormattedTodo[]; count: number }, StoreModel>;

  fetchTodos: Thunk<TodoModel, { accountId: string; needCompleted: boolean }, void, HttpStoreModel, Promise<TodoResponse>>;
  refetchTodos: Thunk<TodoModel, { accountId: string; needCompleted: boolean }, void, HttpStoreModel, Promise<TodoResponse>>;
  setUpcomingInterviewTodos: Action<TodoModel, FormattedTodo[]>;
  fetchUpcomingAppointments: Thunk<
    TodoModel,
    { accountId: string; todoCount?: number; interviewCount?: number; total?: number },
    void,
    HttpStoreModel,
    Promise<TodoResponse>
  >;
  updateStatus: Thunk<
    TodoModel,
    { userId: string; docId: string; status: StatusEnum; category: string; subCategory: string | null; candidateId: string },
    void,
    HttpStoreModel,
    Promise<any>
  >;

  upsertAppointmentTodo: Action<TodoModel, { candidateId?: string; appointment: Appointment }>;
  removeTodoByCandidateOrDocId: Action<TodoModel, { candidateId?: string; docId?: string }>;
}

export const todoModel: TodoModel = {
  todos: [],
  counts: null,
  isLoading: true,
  isRefetching: false,
  openTodoSummary: false,
  filterByStatus: '',
  currentPage: 1,
  pageSize: 20,
  openCandidateMessagingModal: false,
  messagingViewModal: false,
  selectedCandidate: null,
  selectedTodo: null,
  statusFilters: defaultStatusFilters,
  interviewCounts: null,

  setFilterByStatus: action((state, payload) => {
    state.currentPage = 1;
    state.filterByStatus = payload;
  }),

  setOpenTodoSummary: action((state, payload) => {
    state.openTodoSummary = payload;
  }),

  setTodos: action((state, payload) => {
    state.todos = payload;
  }),

  setCounts: action((state, payload) => {
    state.counts = payload;
  }),

  setLoading: action((state, payload) => {
    state.isLoading = payload;
  }),

  setRefetching: action((state, payload) => {
    state.isRefetching = payload;
  }),

  setCurrentPage: action((state, payload) => {
    state.currentPage = payload;
  }),

  setPageSize: action((state, payload) => {
    state.pageSize = payload;
  }),

  setOpenCandidateMessagingModal: action((state, payload) => {
    state.openCandidateMessagingModal = payload;
  }),
  setMessagingViewModal: action((state, payload) => {
    state.messagingViewModal = payload;
  }),

  setSelectedCandidate: action((state, payload) => {
    state.selectedCandidate = payload;
  }),

  setSelectedTodo: action((state, payload) => {
    state.selectedTodo = payload;
  }),

  setStatusFilters: action((state, payload) => {
    state.currentPage = 1;
    state.statusFilters = payload;
  }),

  setInterviewCounts: action((state, payload) => {
    state.interviewCounts = payload;
  }),

  resetState: action((state) => {
    state.todos = [];
    state.counts = null;
    state.isLoading = true;
    state.isRefetching = false;
    state.openTodoSummary = false;
    state.filterByStatus = '';
    state.currentPage = 1;
    state.pageSize = 20;
    state.openCandidateMessagingModal = false;
    state.messagingViewModal = false;
    state.selectedCandidate = null;
    state.selectedTodo = null;
    state.statusFilters = defaultStatusFilters;
    state.interviewCounts = null;
  }),

  paginatedTodos: computed(
    [
      (state) => state.todos,
      (state) => state.filterByStatus,
      (state) => state.statusFilters,
      (state) => state.currentPage,
      (state) => state.pageSize,
      (_state, storeState) => storeState.app.user?.id,
    ],
    (todos, filterByStatus, statusFilters, currentPage, pageSize, userId) => {
      const searchedTodos = filterByStatus
        ? todos.filter((todo) => todo.title.toLowerCase().includes(filterByStatus.toLowerCase()))
        : todos;

      const filteredByStatus = searchedTodos.filter((item) => {
        switch (item.todoStatus) {
          case StatusEnum.TODO:
            return true;

          case StatusEnum.IN_PROGRESS:
            if (!userId) return false;
            return (
              (statusFilters.includes(StatusOptions.IN_PROGRESS_ME) && item.statusAssignedRecruiter === userId) ||
              (statusFilters.includes(StatusOptions.IN_PROGRESS_OTHERS) && item.statusAssignedRecruiter !== userId)
            );

          case StatusEnum.COMPLETED:
            if (!userId) return false;
            return statusFilters.includes(StatusOptions.COMPLETED);

          default:
            return false;
        }
      });

      // const sortedData = orderBy(filteredByStatus, ['todoStatus'], ['desc']);

      // Ensure UPCOMING_INTERVIEWS stay at top, then sort by todoStatus
      const sortedData = orderBy(
        filteredByStatus,
        [(todo) => (todo.title === ToDoTitle.UPCOMING_INTERVIEWS ? 0 : 1), 'todoStatus'],
        ['asc', 'desc'],
      );

      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = sortedData.slice(startIndex, endIndex);

      return { data: paginatedData, count: sortedData.length };
    },
  ),

  fetchTodos: thunk(async (actions, payload, { getStoreState }) => {
    actions.setLoading(true);
    try {
      const controller = new ToDoController(getStoreState().http.client as HttpClientWithRetry);
      const response = await controller.getTodos(payload.accountId, payload.needCompleted);

      const formattedData = orderBy(formatData(response.data.data), ['todoStatus'], ['desc']);
      actions.setTodos(formattedData);
      actions.setCounts(response.data.count);
      return response.data;
    } finally {
      actions.setLoading(false);
    }
  }),

  refetchTodos: thunk(async (actions, payload, { getStoreState }) => {
    actions.setRefetching(true);
    try {
      const controller = new ToDoController(getStoreState().http.client as HttpClientWithRetry);
      const response = await controller.getTodos(payload.accountId, payload.needCompleted);
      const formattedData = formatData(response.data.data);
      actions.setTodos(formattedData);
      actions.setCounts(response.data.count);
      return response.data;
    } finally {
      actions.setRefetching(false);
    }
  }),

  setUpcomingInterviewTodos: action((state, freshAppointments: FormattedTodo[]) => {
    const nonInterviewTodos = state.todos.filter((todo) => todo.title !== ToDoTitle.UPCOMING_INTERVIEWS);
    const combinedTodos = [...nonInterviewTodos, ...freshAppointments];

    // Sort: UPCOMING_INTERVIEWS first, then by todoStatus descending
    state.todos = orderBy(
      combinedTodos,
      [
        (todo) => (todo.title === ToDoTitle.UPCOMING_INTERVIEWS ? 0 : 1), // 0 puts interviews at the top
        'todoStatus',
      ],
      ['asc', 'desc'],
    );
  }),

  fetchUpcomingAppointments: thunk(async (actions, payload, { getStoreState }) => {
    const controller = new ToDoController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.getUpcomingAppointments(
      payload.accountId,
      payload.todoCount,
      payload.interviewCount,
      payload.total,
    );

    const formattedData = formatData(response.data.data);

    // Call the targeted action to swap only interview items
    actions.setUpcomingInterviewTodos(formattedData);
    actions.setInterviewCounts(response.data.count);

    return response.data;
  }),

  updateStatus: thunk(async (actions, payload, { getStoreState, getState }) => {
    const controller = new ToDoController(getStoreState().http.client as HttpClientWithRetry);
    // eslint-disable-next-line no-return-await
    const result = await controller.updateStatus(
      payload.userId,
      payload.docId,
      payload.status,
      payload.category,
      payload.subCategory,
      payload.candidateId,
    );
    const { todos } = getState();
    const updatedTodos = todos.map((todo) =>
      todo.id === payload.docId ? { ...todo, todoStatus: payload.status, statusAssignedRecruiter: payload.userId } : todo,
    );
    actions.setTodos([...updatedTodos]);

    return result;
  }),

  upsertAppointmentTodo: action((state, payload) => {
    if (!payload.candidateId) return;

    const existingIndex = state.todos.findIndex(
      (todo) => todo.candidateId === payload.candidateId && todo.title === ToDoTitle.UPCOMING_INTERVIEWS,
    );

    if (existingIndex !== -1) {
      // Update existing appointment
      state.todos[existingIndex] = {
        ...state.todos[existingIndex],
        appointment: payload.appointment,
      };
    }
  }),

  removeTodoByCandidateOrDocId: action((state, { candidateId, docId }) => {
    state.todos = state.todos.filter((todo) => {
      if (docId) return todo.id !== docId;
      if (candidateId) {
        // Remove upcoming interview todo for one candidate
        return !(todo.candidateId === candidateId && todo.title === ToDoTitle.UPCOMING_INTERVIEWS);
      }
      return true;
    });
  }),
};
